import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import {
  Building2, Boxes, Users, PencilRuler, Crown, Settings2, UsersRound, Layers,
  BarChart3, FileStack, ChevronRight, Database,
} from "lucide-react";

/**
 * OwnerCenter — 1:1 with the mobile app's Admin Console screen.
 *
 * Two sections, each a grid of chunky tap-cards:
 *   • SETUP         — Sites, Site Configuration, People, Crews, Assets, Inspection Builder, Custom Modules
 *   • INSIGHTS & BILLING — Analytics, All Reports
 *
 * Sub-text mirrors the mobile app exactly (e.g. "Manage People · 11 users",
 * "Manage Assets · Fleet · types · models"). Live counts are pulled from the
 * prod API and fall back to a static hint if the endpoint is offline.
 */
export default function OwnerCenter() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    (async () => {
      const [sites, users, assets, templates, crews, subs, reports] = await Promise.allSettled([
        api.get("/admin/sites"),
        api.get("/admin/users"),
        api.get("/assets"),
        api.get("/inspection-templates"),
        api.get("/crews"),
        api.get("/custom-submissions"),
        api.get("/admin/reports-feed"),
      ]);
      const len = (r) => {
        if (r.status !== "fulfilled") return null;
        const d = r.value.data;
        const arr = Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
        return arr.length;
      };
      setCounts({
        sites: len(sites),
        users: len(users),
        assets: len(assets),
        templates: len(templates),
        crews: len(crews),
        subs: len(subs),
        reports: len(reports),
      });
    })().catch(() => {});
  }, []);

  const SETUP = [
    can(user, "manageSites") && {
      to: "/owner/sites",
      icon: Building2,
      label: "Manage Sites",
      sub: countSub(counts.sites, "site", "Add, archive, address & company"),
      testid: "owner-card-sites",
    },
    can(user, "siteConfig") && {
      to: "/owner/site-config",
      icon: Settings2,
      label: "Site Configuration",
      sub: "Shifts · Areas · Hazards · Modules · Mine Setups",
      testid: "owner-card-site-config",
    },
    can(user, "managePeople") && {
      to: "/owner/people",
      icon: Users,
      label: "Manage People",
      sub: countSub(counts.users, "user", "Roles · Pending · PIN reset"),
      testid: "owner-card-people",
    },
    can(user, "crews") && {
      to: "/owner/crews",
      icon: UsersRound,
      label: "Crews",
      sub: countSub(counts.crews, "crew", "Members · Supervisors"),
      testid: "owner-card-crews",
    },
    can(user, "manageAssets") && {
      to: "/owner/assets",
      icon: Boxes,
      label: "Manage Assets",
      sub: "Fleet · types · models",
      testid: "owner-card-assets",
    },
    can(user, "inspectionBuilder") && {
      to: "/owner/inspection-builder",
      icon: PencilRuler,
      label: "Inspection Builder",
      sub: countSub(counts.templates, "template", "Design checks · Expert Builder"),
      testid: "owner-card-inspection-builder",
    },
    can(user, "customModules") && {
      to: "/owner/custom-modules",
      icon: Layers,
      label: "Custom Modules",
      sub: countSub(counts.subs, "submission", "Site-specific forms"),
      testid: "owner-card-custom-modules",
    },
  ].filter(Boolean);

  const INSIGHTS = [
    can(user, "insights") && {
      to: "/insights",
      icon: BarChart3,
      label: "Analytics",
      sub: "Fleet KPIs · Trends · PDF export",
      testid: "owner-card-analytics",
    },
    can(user, "allReports") && {
      to: "/owner/reports",
      icon: FileStack,
      label: "All Reports",
      sub: countSub(counts.reports, "report", "Handovers · Planning Requests · Breakdowns · Emails"),
      testid: "owner-card-reports",
    },
    can(user, "manageAssets") && {
      to: "/owner/backups",
      icon: Database,
      label: "Backups",
      sub: "Daily snapshots · Download · Restore",
      testid: "owner-card-backups",
    },
  ].filter(Boolean);

  return (
    <div className="space-y-8" data-testid="owner-center">
      {/* Hero */}
      <section className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted flex items-center gap-2">
            <Crown className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> Owner Configuration Centre
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-2">
            Everything you own
          </h1>
          <p className="text-muted mt-2 text-sm max-w-xl">
            Sites, people, assets, the inspections that hold it all together — plus every report your crew has ever filed. Templates use a version-locked <span className="text-white font-black">Draft → Preview → Publish</span> workflow so completed reports are never rewritten by later edits.
          </p>
        </div>
      </section>

      <Section title="Setup" testid="owner-section-setup">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SETUP.map((c) => <BigCard key={c.testid} {...c} />)}
        </div>
      </Section>

      <Section title="Insights &amp; Billing" testid="owner-section-insights">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INSIGHTS.map((c) => <BigCard key={c.testid} {...c} />)}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">{title}</div>
        <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
      </div>
      {children}
    </section>
  );
}

function BigCard({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link
      to={to}
      className="panel p-5 flex items-center gap-4 group hover:border-orange-500/50 hover:-translate-y-0.5 transition-all"
      data-testid={testid}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
      >
        <Icon className="w-7 h-7" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-black text-xl tracking-tight leading-tight truncate">
          {label}
        </div>
        <div className="text-xs text-muted font-mono-tight mt-1 truncate">{sub}</div>
      </div>
      <ChevronRight className="w-5 h-5 text-muted group-hover:text-white transition-colors shrink-0" />
    </Link>
  );
}

function countSub(n, singular, fallback) {
  if (n == null) return fallback;
  const plural = singular === "person" ? "people" : `${singular}s`;
  return `${n} ${n === 1 ? singular : plural} · ${fallback}`;
}
